import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Dialog } from './PromptDialogs';

const PLATFORM_LABELS = { ios: 'iOS', android: 'Android' };

const formatDate = (value) => (value ? new Date(value).toLocaleString() : '');

const CodeField = ({ value, onChange }) => (
  <label className="pe-field">
    <span className="pe-field-label">Code from your authenticator app</span>
    <input
      className="pe-input"
      autoComplete="one-time-code"
      placeholder="123456"
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  </label>
);

const KeyDialog = ({ platform, isReplacing, needsCode, busy, error, onSave, onClose }) => {
  const [key, setKey] = useState('');
  const [isShown, setIsShown] = useState(false);
  const [code, setCode] = useState('');
  const label = PLATFORM_LABELS[platform];
  const isReady = key.trim() && (!needsCode || code.trim());

  return (
    <Dialog
      closeOnEscape
      title={`${isReplacing ? 'Replace' : 'Add'} the ${label} key`}
      description="Create a separate key in OpenAI for this app. It is stored encrypted and cannot be read back, here or anywhere else in the admin."
      onClose={onClose}
      footer={(
        <>
          {error && <p className="pe-dialog-error" role="alert">{error}</p>}
          <div className="pe-spacer" />
          <button type="button" className="pe-button" onClick={onClose}>Cancel</button>
          <button type="button" className="pe-button is-primary" disabled={busy || !isReady} onClick={() => onSave(key, code)}>
            {busy ? 'Checking…' : 'Save key'}
          </button>
        </>
      )}
    >
      <label className="pe-field is-first">
        <span className="pe-field-label">OpenAI key</span>
        <span className="pe-key-input">
          <input
            className="pe-input"
            type={isShown ? 'text' : 'password'}
            name={`openai-key-${platform}`}
            autoComplete="off"
            spellCheck={false}
            autoFocus
            placeholder="sk-…"
            value={key}
            onChange={(event) => setKey(event.target.value)}
          />
          <button type="button" className="pe-button" onClick={() => setIsShown((current) => !current)}>{isShown ? 'Hide' : 'Show'}</button>
        </span>
      </label>
      {needsCode && <CodeField value={code} onChange={setCode} />}
    </Dialog>
  );
};

const RemoveDialog = ({ platform, needsCode, busy, error, onRemove, onClose }) => {
  const [code, setCode] = useState('');
  const label = PLATFORM_LABELS[platform];

  return (
    <Dialog
      closeOnEscape
      title={`Remove the ${label} key?`}
      description={`Within a minute, AI requests from ${label} builds that use this server stop working until a new key is added.`}
      onClose={onClose}
      footer={(
        <>
          {error && <p className="pe-dialog-error" role="alert">{error}</p>}
          <div className="pe-spacer" />
          <button type="button" className="pe-button" onClick={onClose}>Cancel</button>
          <button type="button" className="pe-button is-danger" disabled={busy || (needsCode && !code.trim())} onClick={() => onRemove(code)}>Remove key</button>
        </>
      )}
    >
      {needsCode ? <CodeField value={code} onChange={setCode} /> : null}
    </Dialog>
  );
};

/** The OpenAI key of each app. Keys go in and never come back out: only their last characters show. */
const ProviderKeys = () => {
  const { fetchWithAuth } = useAuth();
  const [state, setState] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [dialogError, setDialogError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const response = await fetchWithAuth('/api/admin/ai-keys');
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(data?.error || `Request failed (${response.status})`);
    }
    setState(data);
    setLoadError(null);
    // fetchWithAuth is recreated on every AuthContext render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load().catch((error) => setLoadError(error.message));
  }, [load]);

  const open = (type, platform) => {
    setDialogError(null);
    setNotice(null);
    setDialog({ type, platform });
  };

  const send = async (method, platform, body, onDone) => {
    setBusy(true);
    setDialogError(null);
    try {
      const response = await fetchWithAuth(`/api/admin/ai-keys/${platform}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.error || `Request failed (${response.status})`);
      }
      setDialog(null);
      await load();
      onDone(data);
    } catch (error) {
      setDialogError(error.message);
    } finally {
      setBusy(false);
    }
  };

  const save = (key, code) => send('PUT', dialog.platform, { key, code }, (result) => {
    setNotice(result.checkedWithOpenAI
      ? 'Saved. OpenAI accepted the key, and the API starts using it within a minute.'
      : 'Saved, but OpenAI could not be reached to check the key. The API starts using it within a minute.');
  });

  const remove = (code) => send('DELETE', dialog.platform, { code }, () => setNotice('Key removed.'));

  return (
    <section className="pe-card" aria-label="OpenAI keys">
      <h2>OpenAI keys</h2>
      <p className="pe-muted">The API uses these for AI requests from each app. Replacing a key takes effect within a minute, with no release or restart.</p>

      {loadError && <p className="pe-warning pe-card-message">{loadError}</p>}
      {state && !state.canStore && (
        <p className="pe-warning pe-card-message">This server needs SECRETS_ENCRYPTION_KEY before keys can be stored.</p>
      )}
      {notice && <p className="pe-notice pe-card-message" role="status">{notice}</p>}

      {state && (
        <div className="pe-key-list">
          {state.keys.map((key) => (
            <div key={key.platform} className="pe-key-row">
              <div className="pe-key-name">
                <strong>{PLATFORM_LABELS[key.platform]}</strong>
                {key.configured
                  ? <code>{key.hint}</code>
                  : <span className="pe-badge is-warning">Not set</span>}
              </div>
              <p className="pe-muted">
                {key.configured
                  ? `Updated ${formatDate(key.updatedAt)}${key.updatedBy ? ` by ${key.updatedBy}` : ''}`
                  : `AI requests from ${PLATFORM_LABELS[key.platform]} fail until a key is added.`}
              </p>
              <div className="pe-key-actions">
                {key.configured && <button type="button" className="pe-button" onClick={() => open('remove', key.platform)}>Remove</button>}
                <button
                  type="button"
                  className={key.configured ? 'pe-button' : 'pe-button is-primary'}
                  disabled={!state.canStore}
                  onClick={() => open('save', key.platform)}
                >
                  {key.configured ? 'Replace' : 'Add key'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {dialog?.type === 'save' && (
        <KeyDialog
          platform={dialog.platform}
          isReplacing={state.keys.find((key) => key.platform === dialog.platform)?.configured}
          needsCode={state.twoFactorEnabled}
          busy={busy}
          error={dialogError}
          onSave={save}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.type === 'remove' && (
        <RemoveDialog
          platform={dialog.platform}
          needsCode={state.twoFactorEnabled}
          busy={busy}
          error={dialogError}
          onRemove={remove}
          onClose={() => setDialog(null)}
        />
      )}
    </section>
  );
};

export default ProviderKeys;
